'use client'

import { useEffect, useRef } from 'react'

/**
 * Turns the returned list's children clockwise together with scroll: `--border-rotation` runs
 * 0deg → 360deg while the list's top-to-bottom span crosses the middle of the viewport, and reverses
 * on the way back. Set once on the list and inherited by every child, so they stay in sync; written
 * straight to the element's style, so scrolling never re-renders React.
 * Stays at 0deg (the static design) under prefers-reduced-motion.
 */
export function useScrollBorderRotation<T extends HTMLElement>() {
  const listRef = useRef<T | null>(null)

  useEffect(() => {
    const list = listRef.current
    if (!list) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    let frame: number | null = null

    const update = () => {
      frame = null
      const rect = list.getBoundingClientRect()
      const progress = (window.innerHeight / 2 - rect.top) / rect.height
      const clamped = Math.min(Math.max(progress, 0), 1)
      list.style.setProperty('--border-rotation', `${(clamped * 360).toFixed(1)}deg`)
    }

    const schedule = () => {
      if (frame == null) frame = requestAnimationFrame(update)
    }

    update()
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    return () => {
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
      if (frame != null) cancelAnimationFrame(frame)
    }
  }, [])

  return listRef
}
